import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  createInstallation,
  getBagAddressDetail,
  getFireSafetyModuleSettings,
  getInstallation,
  getInstallationLogbook,
  getInstallations,
  searchBagAddresses,
} from "../../services/fireSafetyLogbookApi";
import { listTenantDocuments } from "../../services/documentLibraryService";

const INSTALLATION_TYPES = [
  { value: "all", label: "Alle types" },
  { value: "bmi", label: "BMI" },
  { value: "oai", label: "OAI" },
  { value: "sprinkler", label: "Sprinkler" },
  { value: "blusgas", label: "Blusgas" },
  { value: "ontruiming", label: "Ontruiming" },
  { value: "overig", label: "Overig" },
];

const CREATE_INSTALLATION_TYPES = INSTALLATION_TYPES.filter((item) => item.value !== "all");

const SERVICE_ID_FILTERS = [
  { value: "all", label: "Alle Service-ID statussen" },
  { value: "active", label: "Service-ID actief" },
  { value: "inactive", label: "Geen Service-ID" },
];

const ACCESS_FILTERS = [
  { value: "all", label: "Alle Access statussen" },
  { value: "active", label: "Access actief" },
  { value: "preparing", label: "Access in voorbereiding" },
];

const DETAIL_TABS = [
  { key: "overzicht", label: "Overzicht" },
  { key: "logboek", label: "Logboek" },
  { key: "service-id", label: "Service-ID" },
  { key: "access", label: "Access" },
  { key: "documenten", label: "Documenten" },
  { key: "historie", label: "Historie" },
];

const EMPTY_FORM = { naam: "", locatie: "", type: "bmi", bagAddress: null };

export default function InstallationsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [moduleEnabled, setModuleEnabled] = useState(false);
  const [installations, setInstallations] = useState([]);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [serviceIdFilter, setServiceIdFilter] = useState("all");
  const [accessFilter, setAccessFilter] = useState("all");
  const [selectedInstallationId, setSelectedInstallationId] = useState("");
  const [detailTab, setDetailTab] = useState("overzicht");
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [detailEntries, setDetailEntries] = useState([]);
  const [detailDocuments, setDetailDocuments] = useState([]);
  const [detailDocumentError, setDetailDocumentError] = useState("");
  const [createPanelOpen, setCreatePanelOpen] = useState(false);
  const [createPanelMode, setCreatePanelMode] = useState("installation");
  const [form, setForm] = useState(EMPTY_FORM);
  const [bagQuery, setBagQuery] = useState("");
  const [bagLoading, setBagLoading] = useState(false);
  const [bagResults, setBagResults] = useState([]);
  const [selectedBagAddress, setSelectedBagAddress] = useState(null);

  useEffect(() => {
    loadPage();
  }, []);

  useEffect(() => {
    const message = String(location.state?.successMessage || "").trim();
    if (!message) return;
    setSuccessMessage(message);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  async function loadPage() {
    try {
      setLoading(true);
      setError("");
      const moduleState = await getFireSafetyModuleSettings();
      setModuleEnabled(Boolean(moduleState.enabled));
      if (!moduleState.enabled) {
        setInstallations([]);
        return;
      }
      const installationList = await getInstallations();
      setInstallations(Array.isArray(installationList) ? installationList : []);
    } catch (err) {
      setError(err?.response?.data?.message || "Installaties laden mislukt");
    } finally {
      setLoading(false);
    }
  }

  const filteredInstallations = useMemo(() => {
    const normalizedQuery = String(query || "").trim().toLowerCase();

    return installations.filter((installation) => {
      if (typeFilter !== "all" && String(installation?.type || "").trim().toLowerCase() !== typeFilter) {
        return false;
      }

      const hasServiceId = Boolean(String(installation?.serviceId || "").trim());
      if (serviceIdFilter === "active" && !hasServiceId) return false;
      if (serviceIdFilter === "inactive" && hasServiceId) return false;

      const accessActive = installation?.qrEnabled === true;
      if (accessFilter === "active" && !accessActive) return false;
      if (accessFilter === "preparing" && accessActive) return false;

      if (!normalizedQuery) return true;

      const haystack = [
        installation?.naam,
        installation?.locatie,
        installation?.bagAddress?.formattedAddress,
        installation?.serviceId,
        installation?.bagAddress?.woonplaatsNaam,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(normalizedQuery);
    });
  }, [accessFilter, installations, query, serviceIdFilter, typeFilter]);

  useEffect(() => {
    if (!filteredInstallations.length) {
      setSelectedInstallationId("");
      setSelectedDetail(null);
      setDetailEntries([]);
      setDetailDocuments([]);
      return;
    }

    if (!selectedInstallationId || !filteredInstallations.some((item) => item._id === selectedInstallationId)) {
      setSelectedInstallationId(filteredInstallations[0]._id);
    }
  }, [filteredInstallations, selectedInstallationId]);

  const selectedInstallation = useMemo(
    () =>
      filteredInstallations.find((item) => item._id === selectedInstallationId) ||
      installations.find((item) => item._id === selectedInstallationId) ||
      null,
    [filteredInstallations, installations, selectedInstallationId]
  );

  const locationInstallationCountMap = useMemo(() => {
    const counts = new Map();
    for (const item of installations) {
      const key = getInstallationLocationKey(item);
      counts.set(key, (counts.get(key) || 0) + 1);
    }
    return counts;
  }, [installations]);

  useEffect(() => {
    let active = true;

    async function loadDetail() {
      if (!selectedInstallation?._id) return;

      try {
        setDetailLoading(true);
        setDetailDocumentError("");

        const detailRes = await getInstallation(selectedInstallation._id);
        let entries = Array.isArray(detailRes?.logbookEntries) ? detailRes.logbookEntries : [];
        if (!entries.length) {
          entries = await getInstallationLogbook(selectedInstallation._id);
        }

        let documents = [];
        let documentError = "";
        try {
          const documentRes = await listTenantDocuments({ assetId: selectedInstallation._id });
          documents = Array.isArray(documentRes?.items) ? documentRes.items : [];
        } catch (documentErr) {
          const status = Number(documentErr?.response?.status || 0);
          documentError =
            status === 403
              ? "Geen toegang tot documenten voor deze installatie."
              : documentErr?.response?.data?.message || "Documenten laden mislukt";
        }

        if (!active) return;
        setSelectedDetail({
          installation: detailRes?.installation || selectedInstallation,
          assets: Array.isArray(detailRes?.assets) ? detailRes.assets : [],
          openPlannedWorkorders: Array.isArray(detailRes?.openPlannedWorkorders)
            ? detailRes.openPlannedWorkorders
            : [],
          historicalLogbookWorkorders: Array.isArray(detailRes?.historicalLogbookWorkorders)
            ? detailRes.historicalLogbookWorkorders
            : [],
        });
        setDetailEntries(Array.isArray(entries) ? entries : []);
        setDetailDocuments(documents);
        setDetailDocumentError(documentError);
      } catch (err) {
        if (!active) return;
        setSelectedDetail({
          installation: selectedInstallation,
          assets: [],
          openPlannedWorkorders: [],
          historicalLogbookWorkorders: [],
        });
        setDetailEntries([]);
        setDetailDocuments([]);
        setDetailDocumentError("");
      } finally {
        if (active) {
          setDetailLoading(false);
        }
      }
    }

    loadDetail();
    return () => {
      active = false;
    };
  }, [selectedInstallation]);

  async function handleCreate(event) {
    event.preventDefault();
    try {
      setSaving(true);
      setError("");
      setSuccessMessage("");
      const installation = await createInstallation(form);
      const nextInstallation = installation || null;
      setForm(EMPTY_FORM);
      setBagQuery("");
      setBagResults([]);
      setSelectedBagAddress(null);
      setCreatePanelOpen(false);
      if (nextInstallation) {
        setInstallations((prev) => [nextInstallation, ...prev]);
        setSelectedInstallationId(nextInstallation._id);
      }
      setSuccessMessage("Installatie toegevoegd aan het register.");
    } catch (err) {
      setError(err?.response?.data?.message || "Installatie aanmaken mislukt");
    } finally {
      setSaving(false);
    }
  }

  async function handleBagSearch(event) {
    if (event?.preventDefault) event.preventDefault();
    try {
      setBagLoading(true);
      setError("");
      const results = await searchBagAddresses(bagQuery);
      setBagResults(Array.isArray(results) ? results : []);
    } catch (err) {
      setBagResults([]);
      setError(err?.response?.data?.message || "BAG zoeken mislukt");
    } finally {
      setBagLoading(false);
    }
  }

  async function handleSelectBagAddress(result) {
    try {
      setBagLoading(true);
      setError("");
      const detail = await getBagAddressDetail(result.id);
      setSelectedBagAddress(detail);
      setForm((prev) => ({
        ...prev,
        locatie: detail?.formattedAddress || prev.locatie,
        bagAddress: detail,
      }));
      setBagQuery(result.omschrijving || detail?.formattedAddress || "");
      setBagResults([]);
    } catch (err) {
      setError(err?.response?.data?.message || "BAG adres laden mislukt");
    } finally {
      setBagLoading(false);
    }
  }

  function clearBagSelection() {
    setSelectedBagAddress(null);
    setBagResults([]);
    setBagQuery("");
    setForm((prev) => ({
      ...prev,
      bagAddress: null,
    }));
  }

  function openCreatePanel(mode) {
    setCreatePanelMode(mode);
    setCreatePanelOpen(true);
  }

  const portfolioSummary = useMemo(() => {
    const locationKeys = new Set(installations.map((item) => getInstallationLocationKey(item)));
    const withServiceId = installations.filter((item) => hasServiceId(item)).length;
    const accessActive = installations.filter((item) => item?.qrEnabled).length;
    const attentionCount = installations.filter((item) => getRegisterAttentionItems(item).length > 0).length;

    return {
      total: installations.length,
      locations: locationKeys.size,
      withServiceId,
      accessActive,
      attentionCount,
    };
  }, [installations]);

  const detailInstallation = selectedDetail?.installation || selectedInstallation || null;
  const detailLocation = detailInstallation ? formatLocationLabel(detailInstallation) : "-";
  const detailWorkorders = (selectedDetail?.openPlannedWorkorders?.length || 0) +
    (selectedDetail?.historicalLogbookWorkorders?.length || 0);
  const detailAttention = useMemo(
    () => getDetailAttentionItems(detailInstallation, detailDocuments, detailEntries),
    [detailDocuments, detailEntries, detailInstallation]
  );
  const detailStatus = useMemo(
    () => getDetailStatus(detailInstallation, detailDocuments, detailEntries),
    [detailDocuments, detailEntries, detailInstallation]
  );
  const detailHistory = useMemo(
    () =>
      buildHistoryRows(
        detailEntries,
        selectedDetail?.openPlannedWorkorders || [],
        selectedDetail?.historicalLogbookWorkorders || [],
        detailDocuments
      ),
    [detailDocuments, detailEntries, selectedDetail]
  );

  if (loading) {
    return <div className="rounded-3xl bg-white p-10 text-sm text-slate-500 shadow-sm">Laden...</div>;
  }

  if (!moduleEnabled) {
    return (
      <div className="rounded-3xl border border-amber-200 bg-amber-50 p-8 text-sm text-amber-900">
        De module Installaties &amp; Logboek staat uit voor deze tenant. Zet hem aan via Tenant Admin.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <section className="rounded-[1.75rem] border border-slate-200 bg-white px-6 py-6 shadow-sm">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Installatieregister</p>
            <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-950">Installatieregister</h1>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              Beheer locaties, installaties, logboekstatus, Service-ID en externe toegang vanuit één overzicht.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => openCreatePanel("installation")}
              className="rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-slate-800"
            >
              + Installatie toevoegen
            </button>
            <button
              type="button"
              onClick={() => openCreatePanel("location")}
              className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:border-slate-400 hover:bg-slate-50"
            >
              Locatie toevoegen
            </button>
          </div>
        </div>
      </section>

      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <StatPill label="installaties" value={portfolioSummary.total} />
        <StatPill label="locaties" value={portfolioSummary.locations} />
        <StatPill label="Service-ID actief" value={portfolioSummary.withServiceId} />
        <StatPill label="Access actief" value={portfolioSummary.accessActive} />
        <StatPill
          label="Aandacht"
          value={portfolioSummary.attentionCount}
          subtitle="te controleren"
          tone={portfolioSummary.attentionCount >= 6 ? "warning" : "neutral"}
        />
      </section>

      {error ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      ) : null}

      {successMessage ? (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {successMessage}
        </div>
      ) : null}

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.58fr)_360px]">
        <section className="rounded-[1.75rem] border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 px-6 py-5">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Locaties en installaties</p>
            <h2 className="mt-1 text-xl font-semibold text-slate-950">Locaties en installaties</h2>
            <p className="mt-1 text-sm text-slate-500">Zoek, filter en open installaties per locatie.</p>
          </div>

          <div className="border-b border-slate-200 px-6 py-4">
            <div className="grid gap-3 lg:grid-cols-[minmax(0,1.4fr)_minmax(180px,0.7fr)_minmax(190px,0.8fr)_minmax(180px,0.8fr)]">
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Zoek op locatie, installatie, adres of Service-ID"
                className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm outline-none transition focus:border-slate-900"
              />
              <FilterSelect value={typeFilter} onChange={setTypeFilter} options={INSTALLATION_TYPES} />
              <FilterSelect value={serviceIdFilter} onChange={setServiceIdFilter} options={SERVICE_ID_FILTERS} />
              <FilterSelect value={accessFilter} onChange={setAccessFilter} options={ACCESS_FILTERS} />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                <tr>
                  <th className="px-6 py-3">Locatie</th>
                  <th className="px-6 py-3">Installatie</th>
                  <th className="px-6 py-3">Type</th>
                  <th className="px-6 py-3">Service-ID</th>
                  <th className="px-6 py-3">Logboek Access</th>
                  <th className="px-6 py-3">Laatst gewijzigd</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3">Aandacht</th>
                  <th className="px-6 py-3">Actie</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white text-sm">
                {filteredInstallations.length ? (
                  filteredInstallations.map((installation) => {
                    const active = installation._id === selectedInstallationId;
                    const attentionItems = getRegisterAttentionItems(installation);
                    const locationCount = locationInstallationCountMap.get(getInstallationLocationKey(installation)) || 1;
                    const registerStatus = getRegisterStatus(installation);
                    return (
                      <tr
                        key={installation._id}
                        onClick={() => setSelectedInstallationId(installation._id)}
                        className={`cursor-pointer transition ${
                          active ? "bg-slate-50" : "hover:bg-slate-50/70"
                        }`}
                      >
                        <td className="px-6 py-3.5 align-top">
                          <div className="font-medium text-slate-900">{formatLocationLabel(installation)}</div>
                          <div className="mt-1 text-xs text-slate-500">
                            {installation?.bagAddress?.woonplaatsNaam || installation?.locatie || "Locatie onbekend"}
                          </div>
                        </td>
                        <td className="px-6 py-3.5 align-top">
                          <div className="font-medium text-slate-900">{installation.naam || "-"}</div>
                          <div className="mt-1 text-xs text-slate-500">
                            {locationCount} {locationCount === 1 ? "installatie" : "installaties"} op locatie
                          </div>
                        </td>
                        <td className="px-6 py-3.5 align-top text-slate-700">
                          {formatInstallationTypeLabel(installation.type)}
                        </td>
                        <td className="px-6 py-3.5 align-top">
                          <StatusPill active={hasServiceId(installation)} activeLabel="Actief" inactiveLabel="Niet actief" />
                        </td>
                        <td className="px-6 py-3.5 align-top">
                          <StatusPill active={installation?.qrEnabled} activeLabel="Actief" inactiveLabel="In voorbereiding" />
                        </td>
                        <td className="px-6 py-3.5 align-top text-slate-600">
                          {formatDateTime(installation.updatedAt || installation.createdAt)}
                        </td>
                        <td className="px-6 py-3.5 align-top">
                          <div className="flex items-center justify-between gap-3">
                            <span
                              className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${registerStatus.tone}`}
                            >
                              {registerStatus.label}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-3.5 align-top">
                          <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                            attentionItems.length ? "bg-slate-100 text-slate-700" : "bg-slate-50 text-slate-500"
                          }`}>
                            {attentionItems.length ? `${attentionItems.length} punt` : "0 punten"}
                          </span>
                        </td>
                        <td className="px-6 py-3.5 align-top">
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              navigate(`/pl/installaties/${installation._id}`);
                            }}
                            className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:border-slate-400 hover:bg-slate-50"
                          >
                            Openen
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={9} className="px-6 py-10 text-center text-sm text-slate-500">
                      Geen installaties gevonden voor de huidige filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <aside className="rounded-[1.75rem] border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 px-5 py-5">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              {detailInstallation ? "Geselecteerde installatie" : "Locatieoverzicht"}
            </p>
            <h2 className="mt-1 text-lg font-semibold text-slate-950">
              {detailInstallation?.naam || "Nog geen installatie geselecteerd"}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              {detailLocation !== "-" ? detailLocation : "Kies een regel uit het register om detailinformatie te tonen."}
            </p>
            {detailInstallation ? (
              <div className="mt-3">
                <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${detailStatusBadge(detailStatus.tone)}`}>
                  {detailStatus.label}
                </span>
              </div>
            ) : null}
          </div>

          {!detailInstallation ? (
            <div className="px-5 py-8 text-sm text-slate-500">Geen installatie geselecteerd.</div>
          ) : (
            <>
              <div className="border-b border-slate-200 px-5 py-5">
                <CompactDetailTable
                  rows={[
                    ["Type", formatInstallationTypeLabel(detailInstallation.type)],
                    ["Service-ID", hasServiceId(detailInstallation) ? "Actief" : "Niet actief"],
                    ["Logboek Access", detailInstallation?.qrEnabled ? "Actief" : "In voorbereiding"],
                    ["Laatste activiteit", formatDateTime(detailInstallation.updatedAt || detailInstallation.createdAt)],
                    ["Werkbonnen", detailLoading ? "Laden..." : String(detailWorkorders)],
                    ["Documenten", detailLoading ? "Laden..." : String(detailDocuments.length)],
                  ]}
                />
              </div>

              <div className="border-b border-slate-200 px-5 py-4">
                <div className="flex flex-wrap gap-2">
                  {DETAIL_TABS.map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => setDetailTab(tab.key)}
                      className={`rounded-md px-2.5 py-1.5 text-sm font-medium transition ${
                        detailTab === tab.key
                          ? "border border-slate-900 bg-slate-950 text-white"
                          : "border border-transparent bg-slate-50 text-slate-600 hover:border-slate-200 hover:bg-white"
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="border-b border-slate-200 px-5 py-5">
                <DetailTabContent
                  tab={detailTab}
                  installation={detailInstallation}
                  detailEntries={detailEntries}
                  detailDocuments={detailDocuments}
                  detailHistory={detailHistory}
                  detailLoading={detailLoading}
                  detailDocumentError={detailDocumentError}
                  workorderCount={detailWorkorders}
                />
              </div>

              <div className="border-b border-slate-200 px-5 py-5">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Acties</p>
                <div className="mt-3 grid gap-2">
                  <ActionLink to={`/pl/installaties/${detailInstallation._id}`}>Installatie openen</ActionLink>
                  <ActionLink to={`/pl/installaties/${detailInstallation._id}?tab=toegang`}>
                    Logboek Access voorbereiden
                  </ActionLink>
                  <ActionLink to={`/pl/installaties/${detailInstallation._id}?tab=werkbonnen`}>
                    Bekijk historie
                  </ActionLink>
                  <ActionLink to={`/pl/installaties/${detailInstallation._id}?tab=documenten`}>
                    Document koppelen
                  </ActionLink>
                </div>
              </div>

              <div className="px-5 py-5">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Aandachtspunten</p>
                <div className="mt-3 space-y-2">
                  {detailAttention.length ? (
                    detailAttention.map((item) => (
                      <div key={item} className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-800">
                        {item}
                      </div>
                    ))
                  ) : (
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm text-emerald-700">
                      Geen aandachtspunten gevonden.
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </aside>
      </div>

      {createPanelOpen ? (
        <div className="fixed inset-0 z-50 flex justify-end bg-slate-950/40">
          <div className="h-full w-full max-w-2xl overflow-y-auto border-l border-slate-200 bg-white shadow-2xl">
            <div className="sticky top-0 z-10 border-b border-slate-200 bg-white px-6 py-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                    {createPanelMode === "location" ? "Locatie toevoegen" : "Nieuwe installatie toevoegen"}
                  </p>
                  <h2 className="mt-1 text-2xl font-semibold text-slate-950">Nieuwe installatie toevoegen</h2>
                  <p className="mt-2 text-sm text-slate-500">
                    Leg eerst de locatie en basisgegevens vast. Daarna kan de installatie verder worden ingericht met logboek, Service-ID, documenten en Access.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setCreatePanelOpen(false)}
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 hover:bg-slate-50"
                >
                  Sluiten
                </button>
              </div>
            </div>

            <form onSubmit={handleCreate} className="space-y-6 px-6 py-6">
              <StepSection
                step="1"
                title="Locatie"
                description="Gebruik BAG waar mogelijk om adressen, objectcontext en gebouwkenmerken consistent vast te leggen."
                active={createPanelMode === "location"}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="text-sm text-slate-500">
                    Nieuwe locaties worden in dit register vastgelegd via de eerste installatie op die locatie.
                  </div>
                  {selectedBagAddress ? (
                    <button
                      type="button"
                      onClick={clearBagSelection}
                      className="rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                    >
                      Wis BAG
                    </button>
                  ) : null}
                </div>

                <div className="mt-4 flex flex-col gap-3 md:flex-row">
                  <input
                    value={bagQuery}
                    onChange={(event) => setBagQuery(event.target.value)}
                    placeholder="Zoek op straat, huisnummer, postcode of plaats"
                    className="flex-1 rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none transition focus:border-slate-900"
                  />
                  <button
                    type="button"
                    onClick={handleBagSearch}
                    disabled={bagLoading}
                    className="rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-medium text-slate-700 transition hover:border-slate-400 hover:bg-slate-50 disabled:opacity-60"
                  >
                    {bagLoading ? "Zoeken..." : "Zoek BAG"}
                  </button>
                </div>

                {bagResults.length > 0 ? (
                  <div className="mt-3 space-y-2">
                    {bagResults.map((result) => (
                      <button
                        key={result.id}
                        type="button"
                        onClick={() => handleSelectBagAddress(result)}
                        className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-left transition hover:border-slate-300 hover:bg-slate-50"
                      >
                        <div className="font-medium text-slate-900">{result.omschrijving}</div>
                        <div className="mt-1 text-xs text-slate-500">Nummeraanduiding: {result.id}</div>
                      </button>
                    ))}
                  </div>
                ) : null}

                {selectedBagAddress ? (
                  <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                    <div className="font-semibold text-slate-900">{selectedBagAddress.formattedAddress}</div>
                    <div className="mt-3 grid gap-x-6 gap-y-2 md:grid-cols-2">
                      <div>Nummeraanduiding: {selectedBagAddress.nummeraanduidingIdentificatie || "-"}</div>
                      <div>Adresseerbaar object: {selectedBagAddress.adresseerbaarObjectIdentificatie || "-"}</div>
                      <div>Woonplaats: {selectedBagAddress.woonplaatsNaam || "-"}</div>
                      <div>Panden: {selectedBagAddress.pandIdentificaties?.length || 0}</div>
                      <div>Gebruiksfunctie: {formatGebruiksdoelen(selectedBagAddress.gebruiksdoelen)}</div>
                      <div>Gebruiksoppervlakte: {formatArea(selectedBagAddress.oppervlakte)}</div>
                    </div>
                  </div>
                ) : null}
              </StepSection>

              <StepSection
                step="2"
                title="Installatie"
                description="Leg de basisgegevens vast zodat de installatie direct zichtbaar wordt in het register."
              >
                <div className="grid gap-4">
                  <label className="block">
                    <span className="mb-2 block text-sm font-medium text-slate-700">Installatienaam</span>
                    <input
                      value={form.naam}
                      onChange={(event) => setForm((prev) => ({ ...prev, naam: event.target.value }))}
                      required
                      className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none transition focus:border-slate-900"
                      placeholder="Bijv. BMI hoofdgebouw"
                    />
                  </label>

                  <label className="block">
                    <span className="mb-2 block text-sm font-medium text-slate-700">Locatieomschrijving</span>
                    <input
                      value={form.locatie}
                      onChange={(event) => setForm((prev) => ({ ...prev, locatie: event.target.value }))}
                      className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none transition focus:border-slate-900"
                      placeholder="Bijv. Hoofdingang, verdieping 1"
                    />
                    {selectedBagAddress ? (
                      <span className="mt-2 block text-xs text-slate-500">
                        Voorgevuld vanuit BAG. Deze omschrijving blijft verfijnbaar voor operationeel gebruik.
                      </span>
                    ) : null}
                  </label>

                  <label className="block">
                    <span className="mb-2 block text-sm font-medium text-slate-700">Installatietype</span>
                    <select
                      value={form.type}
                      onChange={(event) => setForm((prev) => ({ ...prev, type: event.target.value }))}
                      className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none transition focus:border-slate-900"
                    >
                      {CREATE_INSTALLATION_TYPES.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </StepSection>

              <StepSection
                step="3"
                title="Beheeropties"
                description="Na registratie wordt de installatie verder ingericht vanuit het installatieregister."
              >
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                  Service-ID, Logboek Access, documenten en historie blijven aanwezig als vervolgstappen, maar domineren het registratiescherm niet.
                </div>
              </StepSection>

              <div className="flex items-center justify-between gap-3 border-t border-slate-200 pt-6">
                <button
                  type="button"
                  onClick={() => setCreatePanelOpen(false)}
                  className="rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm font-medium text-slate-700 transition hover:border-slate-400 hover:bg-slate-50"
                >
                  Annuleren
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-xl bg-slate-950 px-5 py-3 text-sm font-medium text-white transition hover:bg-slate-800 disabled:opacity-60"
                >
                  {saving ? "Installatie wordt toegevoegd..." : "Installatie toevoegen"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function StatPill({ label, value, subtitle = "", tone = "neutral" }) {
  return (
    <div
      className={`rounded-2xl border px-4 py-4 shadow-sm ${
        tone === "warning"
          ? "border-amber-200 bg-amber-50"
          : "border-slate-200 bg-white"
      }`}
    >
      <div className="text-2xl font-semibold tracking-tight text-slate-950">{value}</div>
      <div className="mt-1 text-sm text-slate-600">{label}</div>
      {subtitle ? <div className="mt-1 text-xs text-slate-500">{subtitle}</div> : null}
    </div>
  );
}

function FilterSelect({ value, onChange, options }) {
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm outline-none transition focus:border-slate-900"
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

function StatusPill({ active, activeLabel, inactiveLabel }) {
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
        active
          ? "bg-emerald-50 text-emerald-700"
          : "bg-slate-100 text-slate-600"
      }`}
    >
      {active ? activeLabel : inactiveLabel}
    </span>
  );
}

function CompactDetailTable({ rows }) {
  return (
    <table className="min-w-full text-sm">
      <tbody className="divide-y divide-slate-200">
        {rows.map(([label, value]) => (
          <tr key={label}>
            <td className="w-1/2 py-2.5 pr-4 text-slate-500">{label}</td>
            <td className="py-2.5 font-medium text-slate-900">{value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DetailNote({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 px-3 py-3">
      <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">{label}</div>
      <div className="mt-1 text-sm text-slate-700">{value}</div>
    </div>
  );
}

function detailStatusBadge(tone) {
  if (tone === "ok") return "bg-emerald-50 text-emerald-700";
  if (tone === "warning") return "bg-amber-50 text-amber-700";
  return "bg-slate-100 text-slate-700";
}

function ActionLink({ to, children }) {
  return (
    <Link
      to={to}
      className="rounded-xl border border-slate-200 px-3 py-3 text-sm font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
    >
      {children}
    </Link>
  );
}

function StepSection({ step, title, description, active = false, children }) {
  return (
    <section className={`rounded-[1.5rem] border p-5 ${active ? "border-slate-900 bg-slate-50/70" : "border-slate-200 bg-white"}`}>
      <div className="flex items-start gap-4">
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${active ? "bg-slate-950 text-white" : "bg-slate-100 text-slate-700"}`}>
          {step}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-lg font-semibold text-slate-950">{title}</h3>
          <p className="mt-1 text-sm text-slate-500">{description}</p>
          <div className="mt-4">{children}</div>
        </div>
      </div>
    </section>
  );
}

function DetailTabContent({
  tab,
  installation,
  detailEntries,
  detailDocuments,
  detailHistory,
  detailLoading,
  detailDocumentError,
  workorderCount,
}) {
  if (detailLoading) {
    return <div className="text-sm text-slate-500">Detail laden...</div>;
  }

  if (tab === "overzicht") {
    const overviewAttention = getDetailAttentionItems(installation, detailDocuments, detailEntries);
    const nextAction = getNextActionLabel(installation, detailDocuments, detailEntries);
    return (
      <div className="space-y-4 text-sm text-slate-600">
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
          <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Volgende stap</div>
          <div className="mt-1 font-medium text-slate-900">{nextAction}</div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <DetailNote label="Service-ID" value={hasServiceId(installation) ? "Actief en zichtbaar in register." : "Nog niet aangemaakt."} />
          <DetailNote label="Logboek Access" value={installation?.qrEnabled ? "Externe toegang staat actief." : "Voorbereiding of activatie nog nodig."} />
          <DetailNote label="Documenten" value={detailDocuments.length ? `${detailDocuments.length} gekoppeld.` : "Nog geen documenten gekoppeld."} />
          <DetailNote label="Logboek" value={detailEntries.length ? `${detailEntries.length} regels geregistreerd.` : "Nog geen logboekactiviteit."} />
        </div>
        <div className="rounded-xl border border-slate-200 px-3 py-3">
          <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Aandacht nu</div>
          <div className="mt-2 space-y-2">
            {overviewAttention.length ? (
              overviewAttention.slice(0, 3).map((item) => (
                <div key={item} className="text-sm text-slate-700">
                  {item}
                </div>
              ))
            ) : (
              <div className="text-sm text-emerald-700">Geen directe aandachtspunten.</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (tab === "logboek") {
    return (
      <div className="space-y-3">
        <div className="text-sm text-slate-600">{detailEntries.length} logboekregels gevonden.</div>
        {detailEntries.length ? (
          detailEntries.slice(0, 3).map((entry) => (
            <div key={entry._id} className="rounded-xl border border-slate-200 px-3 py-3 text-sm">
              <div className="font-medium text-slate-900">{formatEntryType(entry.entryType)}</div>
              <div className="mt-1 text-slate-600">{entry.description || "-"}</div>
              <div className="mt-2 text-xs text-slate-500">{formatDateTime(entry.createdAt || entry.performedAt)}</div>
            </div>
          ))
        ) : (
          <div className="rounded-xl border border-dashed border-slate-300 px-3 py-4 text-sm text-slate-500">
            Nog geen logboekregels geladen.
          </div>
        )}
      </div>
    );
  }

  if (tab === "service-id") {
    return (
      <div className="space-y-2 text-sm text-slate-600">
        <p>Service-ID status: <span className="font-medium text-slate-900">{hasServiceId(installation) ? "Actief" : "Niet actief"}</span></p>
        <p>Huidige Service-ID: <span className="font-medium text-slate-900">{installation?.serviceId || "-"}</span></p>
        <p>Gebruik de actieknoppen hieronder om dit verder te beheren in het installatieprofiel.</p>
      </div>
    );
  }

  if (tab === "access") {
    return (
      <div className="space-y-2 text-sm text-slate-600">
        <p>Logboek Access: <span className="font-medium text-slate-900">{installation?.qrEnabled ? "Actief" : "In voorbereiding"}</span></p>
        <p>QR-token: <span className="font-medium text-slate-900">{installation?.qrToken || "-"}</span></p>
        <p>Externe toegang blijft onderdeel van hetzelfde register en wordt niet als losse module getoond.</p>
      </div>
    );
  }

  if (tab === "documenten") {
    return (
      <div className="space-y-3">
        {detailDocumentError ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-800">
            {detailDocumentError}
          </div>
        ) : null}
        <div className="text-sm text-slate-600">{detailDocuments.length} documenten gekoppeld.</div>
        {detailDocuments.length ? (
          detailDocuments.slice(0, 3).map((item) => (
            <div key={item._id} className="rounded-xl border border-slate-200 px-3 py-3 text-sm">
              <div className="font-medium text-slate-900">{item.title || "-"}</div>
              <div className="mt-1 text-slate-600">{item?.category?.label || formatDocumentType(item.documentType)}</div>
            </div>
          ))
        ) : (
          <div className="rounded-xl border border-dashed border-slate-300 px-3 py-4 text-sm text-slate-500">
            Geen documenten gekoppeld.
          </div>
        )}
      </div>
    );
  }

  if (tab === "historie") {
    return (
      <div className="space-y-3">
        <div className="text-sm text-slate-600">
          {detailHistory.length} gebeurtenissen, {workorderCount} werkbonnen en {detailEntries.length} logboekregels in beeld.
        </div>
        {detailHistory.length ? (
          detailHistory.slice(0, 4).map((item) => (
            <div key={item.id} className="rounded-xl border border-slate-200 px-3 py-3 text-sm">
              <div className="flex items-center justify-between gap-3">
                <div className="font-medium text-slate-900">{item.title}</div>
                <div className="text-xs text-slate-500">{formatDateTime(item.date)}</div>
              </div>
              <div className="mt-1 text-slate-600">{item.detail}</div>
            </div>
          ))
        ) : (
          <div className="rounded-xl border border-dashed border-slate-300 px-3 py-4 text-sm text-slate-500">
            Nog geen historie geladen.
          </div>
        )}
      </div>
    );
  }

  return null;
}

function hasServiceId(installation) {
  return Boolean(String(installation?.serviceId || "").trim());
}

function hasBagContext(installation) {
  return Boolean(String(installation?.bagAddress?.formattedAddress || "").trim());
}

function hasRecentActivity(installation, entries = []) {
  const dates = [installation?.updatedAt, installation?.createdAt, entries?.[0]?.createdAt, entries?.[0]?.performedAt]
    .filter(Boolean)
    .map((value) => new Date(value).getTime())
    .filter((value) => Number.isFinite(value) && value > 0);

  if (!dates.length) return false;
  const latest = Math.max(...dates);
  const ninetyDays = 90 * 24 * 60 * 60 * 1000;
  return Date.now() - latest <= ninetyDays;
}

function getRegisterStatus(installation) {
  const attention = getRegisterAttentionItems(installation);
  if (attention.length >= 2) {
    return { label: "Aandacht nodig", tone: "bg-amber-50 text-amber-700" };
  }
  if (!hasServiceId(installation) || !installation?.qrEnabled) {
    return { label: "Voorbereiden", tone: "bg-slate-100 text-slate-700" };
  }
  return { label: "Operationeel", tone: "bg-emerald-50 text-emerald-700" };
}

function getDetailStatus(installation, documents = [], entries = []) {
  if (!installation) return { label: "-", tone: "muted" };

  const attention = getDetailAttentionItems(installation, documents, entries);
  if (attention.length >= 3) return { label: "Aandacht nodig", tone: "warning" };
  if (hasServiceId(installation) && installation?.qrEnabled && documents.length > 0 && hasRecentActivity(installation, entries)) {
    return { label: "Operationeel", tone: "ok" };
  }
  if (hasServiceId(installation) || installation?.qrEnabled || documents.length > 0) {
    return { label: "In opbouw", tone: "muted" };
  }
  return { label: "Basisregistratie", tone: "muted" };
}

function getRegisterAttentionItems(installation) {
  const items = [];
  if (!hasServiceId(installation)) items.push("Geen Service-ID");
  if (!installation?.qrEnabled) items.push("Access nog niet actief");
  if (!hasBagContext(installation)) items.push("Geen gebouwkenmerken gekoppeld");
  return items;
}

function getDetailAttentionItems(installation, documents, entries) {
  if (!installation) return [];
  const items = getRegisterAttentionItems(installation);
  if (Array.isArray(documents) && documents.length === 0) {
    items.push("Geen documenten gekoppeld");
  }
  if (Array.isArray(entries) && entries.length === 0) {
    items.push("Geen recente activiteit");
  } else if (!hasRecentActivity(installation, entries)) {
    items.push("Geen recente activiteit");
  }
  return Array.from(new Set(items));
}

function getNextActionLabel(installation, documents = [], entries = []) {
  if (!installation) return "-";
  if (!hasServiceId(installation)) return "Service-ID aanmaken of koppelen.";
  if (!installation?.qrEnabled) return "Logboek Access voorbereiden.";
  if (!documents.length) return "Eerste documenten koppelen.";
  if (!entries.length) return "Eerste logboekactiviteit registreren.";
  if (!hasRecentActivity(installation, entries)) return "Controleer recente activiteit en werk dossier bij.";
  return "Dossier is bruikbaar; open installatie voor verdieping.";
}

function buildHistoryRows(entries, openWorkorders, historicalWorkorders, documents) {
  const entryRows = (entries || []).map((entry) => ({
    id: `entry-${entry?._id || entry?.createdAt}`,
    date: entry?.createdAt || entry?.performedAt || null,
    title: formatEntryType(entry?.entryType),
    detail: entry?.description || "-",
  }));

  const workorderRows = [...(openWorkorders || []), ...(historicalWorkorders || [])].map((item) => ({
    id: `workorder-${item?._id || item?.id}`,
    date: item?.updatedAt || item?.createdAt || null,
    title: item?.werkbonNummer || item?.nummer || "Werkbon",
    detail: item?.omschrijving || "Werkbonhistorie",
  }));

  const documentRows = (documents || []).map((item) => ({
    id: `document-${item?._id}`,
    date: item?.updatedAt || item?.createdAt || null,
    title: item?.title || "Document",
    detail: formatDocumentType(item?.documentType),
  }));

  return [...entryRows, ...workorderRows, ...documentRows].sort((a, b) => {
    const left = new Date(a.date || 0).getTime();
    const right = new Date(b.date || 0).getTime();
    return right - left;
  });
}

function getInstallationLocationKey(installation) {
  const nummeraanduiding = String(installation?.bagAddress?.nummeraanduidingIdentificatie || "").trim();
  if (nummeraanduiding) return `bag:${nummeraanduiding}`;
  const address = String(installation?.bagAddress?.formattedAddress || installation?.locatie || "").trim();
  return address ? `loc:${address.toLowerCase()}` : `installation:${installation?._id || ""}`;
}

function formatLocationLabel(installation) {
  return (
    installation?.bagAddress?.formattedAddress ||
    installation?.locatie ||
    "Locatie onbekend"
  );
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("nl-NL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function formatGebruiksdoelen(values) {
  if (!Array.isArray(values) || values.length === 0) return "-";
  return values.join(", ");
}

function formatArea(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return "-";
  return `${amount.toLocaleString("nl-NL")} m2`;
}

function formatInstallationTypeLabel(value) {
  const normalized = String(value || "").trim().toLowerCase();
  const match = CREATE_INSTALLATION_TYPES.find((item) => item.value === normalized);
  return match?.label || (normalized ? normalized.toUpperCase() : "-");
}

function formatEntryType(value) {
  const labels = {
    inspectie: "Inspectie",
    onderhoud: "Onderhoud",
    beheer: "Beheer",
    storing: "Storing",
    test: "Test",
    wijziging: "Wijziging",
  };
  return labels[String(value || "").trim().toLowerCase()] || "Logboekregel";
}

function formatDocumentType(value) {
  const labels = {
    inspection_report: "Inspectierapport",
    asset_document: "Installatiedocument",
    certificate: "Certificaat",
    drawing: "Tekening",
    permit: "Vergunning",
    general: "Algemeen",
  };
  return labels[String(value || "").trim().toLowerCase()] || "Document";
}
